import { Route, Routes } from "react-router-dom";
import { AnnouncementBar } from "./components/AnnouncementBar";
import { SiteHeader } from "./components/SiteHeader";
import { SiteFooter } from "./components/SiteFooter";
import { ScrollToTop } from "./components/ScrollToTop";
import { Home } from "./pages/Home";
import { DirectoryPage } from "./pages/DirectoryPage";
import { OutcomePage } from "./pages/OutcomePage";
import { GlossaryPage } from "./pages/GlossaryPage";
import { LibraryPage } from "./pages/LibraryPage";
import { CompanyPage } from "./pages/CompanyPage";
import { ContactPage } from "./pages/ContactPage";
import { DemosPage } from "./pages/DemosPage";
import { PillarPage } from "./pages/PillarPage";
import { ResearchPage } from "./pages/ResearchPage";
import { ProductPage } from "./pages/ProductPage";
import { ServicePage } from "./pages/ServicePage";
import { WardenPage } from "./pages/WardenPage";
import { StubPage } from "./pages/StubPage";
import { NotFound } from "./pages/NotFound";
import { BifrostPage } from "./pages/bifrost/BifrostPage";
import { LegalPage } from "./pages/LegalPage";
import { legalPage } from "./content/bifrost-legal";
import { GuardianPage } from "./pages/guardian/GuardianPage";
import { bifrostPage } from "./content/bifrost-site";
import { SentiencePage } from "./pages/sentience/SentiencePage";
import { sentiencePage } from "./content/sentience-site";
import { guardianPage } from "./content/guardian-site";
import {
  directoryPage,
  glossaryPage,
  libraryPage,
  outcomePage,
  pillarPage,
  productPage,
  researchPage,
  servicePage,
  wardenHub,
} from "./content";
import { ALL_PRODUCTS } from "./nav/navigation";
import { ROUTES, type RouteDef } from "./routes";

/**
 * Content, where it exists, wins over the placeholder. Routes stay in one table
 * so navigation can never point at a path the router does not know.
 */
function elementFor(route: RouteDef) {
  if (route.path === "/contact") return <ContactPage />;
  if (route.path === "/company") return <CompanyPage />;
  if (route.path === "/demos") return <DemosPage />;

  const legal = legalPage(route.path);
  if (legal) return <LegalPage page={legal} />;

  const bifrost = bifrostPage(route.path);
  if (bifrost) return <BifrostPage page={bifrost} />;

  const guardian = guardianPage(route.path);
  if (guardian) return <GuardianPage page={guardian} />;

  const sentience = sentiencePage(route.path);
  if (sentience) return <SentiencePage page={sentience} />;

  const pillar = pillarPage(route.path);
  if (pillar) return <PillarPage content={pillar} />;

  const directory = directoryPage(route.path);
  if (directory) return <DirectoryPage content={directory} />;

  const outcome = outcomePage(route.path);
  if (outcome) return <OutcomePage content={outcome} />;

  const warden = wardenHub(route.path);
  if (warden) return <WardenPage content={warden} />;

  const service = servicePage(route.path);
  if (service) return <ServicePage content={service} />;

  const research = researchPage(route.path);
  if (research) return <ResearchPage content={research} />;

  const glossary = glossaryPage(route.path);
  if (glossary) return <GlossaryPage content={glossary} />;

  const library = libraryPage(route.path);
  if (library) return <LibraryPage content={library} />;

  const slug = ALL_PRODUCTS.find((p) => p.href === route.path)?.slug;
  const product = slug ? productPage(slug) : undefined;
  if (product) return <ProductPage content={product} />;

  return <StubPage route={route} />;
}

export function App() {
  return (
    <>
      <a className="skip" href="#main">
        Skip to content
      </a>
      <AnnouncementBar />
      <SiteHeader />
      <ScrollToTop />
      <Routes>
        <Route path="/" element={<Home />} />
        {ROUTES.map((route) => (
          <Route
            key={route.path}
            path={route.path}
            element={elementFor(route)}
          />
        ))}
        <Route path="*" element={<NotFound />} />
      </Routes>
      <SiteFooter />
    </>
  );
}
